import { useNostr } from '@nostrify/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

const PAGE_SIZE = 20;

interface PostsPage {
  events: NostrEvent[];
  oldest?: number;
}

/**
 * Fetches kind-1 notes authored by `pubkey`, paginated by `until` cursor.
 * Loosely inspired by ditto's useProfileFeed, stripped down to the essentials:
 * single author, kind 1 only, no reposts, no replies filter at the relay level
 * (we keep top-level posts only by dropping events with an NIP-10 "e" tag).
 */
export function useMyPosts(pubkey: string | undefined, opts?: { includeReplies?: boolean }) {
  const { nostr } = useNostr();
  const includeReplies = opts?.includeReplies ?? false;

  return useInfiniteQuery<PostsPage, Error>({
    queryKey: ['my-posts', pubkey ?? '', includeReplies],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam, signal }) => {
      if (!pubkey) return { events: [], oldest: undefined };

      const filter: NostrFilter = {
        kinds: [1],
        authors: [pubkey],
        limit: PAGE_SIZE,
      };
      if (typeof pageParam === 'number') {
        filter.until = pageParam;
      }

      const querySignal = AbortSignal.any([signal, AbortSignal.timeout(8000)]);
      const raw = await nostr.query([filter], { signal: querySignal });

      const now = Math.floor(Date.now() / 1000);
      const filtered = raw
        .filter((e) => e.created_at <= now)
        .filter((e) => includeReplies || !hasReplyTag(e))
        .sort((a, b) => b.created_at - a.created_at);

      // Dedupe (in case of duplicate events across queries)
      const seen = new Set<string>();
      const events: NostrEvent[] = [];
      for (const e of filtered) {
        if (!seen.has(e.id)) {
          seen.add(e.id);
          events.push(e);
        }
      }

      return {
        events,
        oldest: events.at(-1)?.created_at,
      };
    },
    getNextPageParam: (last) => {
      if (last.events.length === 0 || last.oldest === undefined) return undefined;
      return last.oldest - 1;
    },
    enabled: !!pubkey,
    staleTime: 30_000,
  });
}

function hasReplyTag(event: NostrEvent): boolean {
  // A reply has an "e" tag (NIP-10) — any "e" tag signals this note references another.
  // For a top-level profile feed we drop these.
  return event.tags.some(([name]) => name === 'e');
}
